import { useState } from "react";

export const landmarks = [
  { name: "Palm Beach International Airport (PBI)", icon: "ri-flight-takeoff-line", distance: "Primary airport — within West Palm Beach", desc: "Efficient, award-winning South Florida airport serving American, Delta, JetBlue, Southwest, United, and international carriers. The primary departure point for Palm Beach County travelers.", url: "https://www.pbia.org/", schemaType: "Airport" },
  { name: "Port of Palm Beach", icon: "ri-anchor-line", distance: "~3 miles — Riviera Beach", desc: "Multi-purpose seaport handling container cargo, dry bulk, and cruise operations. Serves as a departure point for Bahamas Celebration cruises and Caribbean-bound vessels.", url: "https://www.portofpalmbeach.com/", schemaType: "TouristAttraction" },
  { name: "Clematis Street", icon: "ri-store-2-line", distance: "Downtown West Palm Beach", desc: "Historic downtown shopping, dining, and nightlife district with live music venues, restaurants, bars, and the weekly Clematis by Night event. The cultural heart of West Palm Beach.", url: "https://www.downtownwpb.com/clematis-street/", schemaType: "TouristAttraction" },
  { name: "Worth Avenue", icon: "ri-store-2-line", distance: "~2 miles — Palm Beach island", desc: "Legendary luxury shopping avenue in Palm Beach, featuring designer boutiques like Gucci, Chanel, and Hermès alongside art galleries and fine dining. A world-famous destination for affluent travelers.", url: "https://www.worth-avenue.com/", schemaType: "TouristAttraction" },
  { name: "Norton Museum of Art", icon: "ri-building-line", distance: "Downtown — S Dixie Hwy", desc: "Internationally recognized art museum housing over 8,200 works spanning European, American, Chinese, and Contemporary art. A major cultural institution in South Florida.", url: "https://www.norton.org/", schemaType: "TouristAttraction" },
  { name: "Raymond F. Kravis Center for the Performing Arts", icon: "ri-building-line", distance: "Downtown — Okeechobee Blvd", desc: "Premier performing arts venue hosting Broadway shows, concerts, ballet, opera, and touring productions. A cultural anchor drawing visitors from across Palm Beach County.", url: "https://www.kravis.org/", schemaType: "TouristAttraction" },
  { name: "Flagler Museum (Whitehall)", icon: "ri-bank-line", distance: "~2 miles — Palm Beach island", desc: "Gilded Age mansion-turned-museum built by Henry Flagler in 1902, showcasing 75 rooms of original furnishings, art, and railroad history. A National Historic Landmark.", url: "https://www.flaglermuseum.us/", schemaType: "TouristAttraction" },
  { name: "Brightline West Palm Beach Station", icon: "ri-train-line", distance: "Downtown — Evernia St", desc: "High-speed rail station connecting West Palm Beach to Fort Lauderdale and Miami in under an hour. A modern transportation hub transforming regional travel in South Florida.", url: "https://www.gobrightline.com/", schemaType: "CivicStructure" },
  { name: "Fort Lauderdale-Hollywood Intl (FLL)", icon: "ri-flight-takeoff-line", distance: "~50 miles south via I-95", desc: "Larger international hub with more budget carrier options and Latin America routes. A common alternative for West Palm Beach travelers seeking additional flight options.", url: "https://www.broward.org/airport/", schemaType: "Airport" },
  { name: "Interstate 95 (I-95)", icon: "ri-road-map-line", distance: "Runs directly through West Palm Beach", desc: "Primary north-south highway connecting West Palm Beach to Fort Lauderdale, Miami, and the entire Eastern Seaboard. Essential for airport access and regional travel along Florida's Gold Coast.", url: "https://www.fdot.gov/", schemaType: "CivicStructure" },
];

const photoLocations = [
  { name: "CVS Pharmacy — Okeechobee Blvd", address: "1960 Okeechobee Blvd, West Palm Beach, FL 33409", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — Palm Beach Lakes Blvd", address: "1901 Palm Beach Lakes Blvd, West Palm Beach, FL 33401", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — S Dixie Hwy", address: "700 S Dixie Hwy, West Palm Beach, FL 33401", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — N Military Trl", address: "4201 N Military Trl, West Palm Beach, FL 33407", hours: "Call for hours" },
  { name: "Walgreens — Forest Hill Blvd", address: "6600 Forest Hill Blvd, Greenacres, FL 33413", hours: "Open 24 Hours" },
  { name: "Walgreens — N Congress Ave", address: "6901 N Congress Ave, Lake Worth, FL 33462", hours: "Open 24 Hours" },
  { name: "The UPS Store — Palm Beach Lakes", address: "1850 Palm Beach Lakes Blvd, Ste 100, West Palm Beach, FL 33401", hours: "Call for hours" },
  { name: "The UPS Store — Okeechobee Blvd", address: "1515 Okeechobee Blvd, Ste B-100, West Palm Beach, FL 33401", hours: "Call for hours" },
  { name: "FedEx Office — Downtown WPB", address: "400 Hibiscus St, West Palm Beach, FL 33401", hours: "Call for hours" },
  { name: "FedEx Office — Palm Beach Lakes", address: "1850 Palm Beach Lakes Blvd, West Palm Beach, FL 33401", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Palm Beach County Tax Collector — West Palm Beach",
    address: "301 N Olive Ave, West Palm Beach, FL 33401",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, titles & registration. Appointment recommended.",
    featured: true,
  },
  {
    name: "Palm Beach County Tax Collector — Royal Palm Beach",
    address: "200 Civic Center Way, Royal Palm Beach, FL 33411",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Western Palm Beach County option. Appointment recommended.",
    featured: false,
  },
  {
    name: "Palm Beach County Tax Collector — Boynton Beach",
    address: "301 N Congress Ave, Boynton Beach, FL 33426",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Southern Palm Beach County option. Appointment recommended.",
    featured: false,
  },
];

export default function WestPalmHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for West Palm Beach Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local West Palm Beach resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in West Palm Beach</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations in West Palm Beach include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Palm Beach County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Palm Beach County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Palm Beach County. Online, mail, or in-person options available.", url: "https://palmbeach.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Palm Beach County. Required for estate and legal matters.", url: "https://palmbeach.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Palm Beach County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.mypalmbeachclerk.com/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Palm Beach County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.mypalmbeachclerk.com/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Palm Beach County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Palm Beach County Tax Collector — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver&apos;s license or REAL ID, the Palm Beach County Tax Collector handles DMV services in the West Palm Beach area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.pbcgov.com/taxcollector/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Palm Beach County Tax Collector — DMV Services
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Palm Beach County Tax Collector website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving West Palm Beach Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                West Palm Beach is served by Palm Beach International Airport (PBI), one of the most efficient airports in South Florida. Fort Lauderdale-Hollywood International is also within driving distance for additional flight options.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "PBI",
                    name: "Palm Beach International Airport",
                    distance: "Primary airport — located in West Palm Beach",
                    address: "1000 Turnage Blvd, West Palm Beach, FL 33406",
                    note: "Efficient South Florida airport with direct international service. Serves American, Delta, JetBlue, Southwest, United, and international carriers.",
                    flightUrl: "https://www.pbia.org/",
                    flightLabel: "PBI Airport Website",
                    featured: true,
                  },
                  {
                    code: "FLL",
                    name: "Fort Lauderdale-Hollywood International",
                    distance: "~50 miles south on I-95",
                    address: "100 Terminal Dr, Fort Lauderdale, FL 33315",
                    note: "Larger hub with more international routes and budget carrier options. Backup option for West Palm Beach travelers.",
                    flightUrl: "https://www.broward.org/airport/",
                    flightLabel: "FLL Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {/* Nearby Landmarks */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-landscape-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near West Palm Beach</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                West Palm Beach and its neighboring island of Palm Beach form one of the most iconic travel corridors in South Florida. From the luxury shopping of Worth Avenue to the Gilded Age splendor of the Flagler Museum, and from the convenience of Brightline high-speed rail to the efficiency of Palm Beach International Airport — these landmarks define travel patterns for Palm Beach County residents and visitors.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                {landmarks.map((landmark, i) => (
                  <a
                    key={i}
                    href={landmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                        <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                        <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international travel.</strong> Whether you are flying out of PBI on an international flight, sailing from the Port of Palm Beach, or boarding Brightline for a Miami departure — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for West Palm Beach residents.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}